import PaginationNumeric from "@shared/components/PaginationNumeric";
import { TestProgressStatus } from "@shared/enums/test.enum";
import theme from "@shared/utils/themeColors";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthenticatedLayout from "../../../shared/layouts/AuthenticatedLayout";
import { TestCard } from "../components/TestCard";
import { TestService } from "../services/test.service";

export default function Tests() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["tests", page, searchQuery, statusFilter],
    queryFn: () =>
      TestService.getTests({
        page,
        searchQuery,
        statusFilter,
      }),
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnReconnect: true,
    gcTime: 0,
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  const handleStartTest = (testId: string, resume?: boolean) => {
    navigate(`/tests/${testId}/take`);
  };

  return (
    <AuthenticatedLayout
      isLoading={isLoading || isRefreshing}
      error={error}
      isEmpty={!data?.tests || data.tests.length === 0}
      emptyStateProps={{
        title: "No tests found",
        description:
          searchQuery || statusFilter
            ? "No tests match your search criteria"
            : "Start by creating your first test",
        icon: (
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-t from-gray-200 to-gray-100 mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className={`w-8 h-8 ${theme.text.muted}`}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        ),
      }}
      pageHeader={{
        title: "Practice Tests",
        search: {
          show: true,
          placeholder: "Search tests...",
          onChange: setSearchQuery,
        },
        filter: {
          show: true,
          options: [
            { label: "All", value: "" },
            { label: "New", value: TestProgressStatus.NEW },
            { label: "In Progress", value: TestProgressStatus.IN_PROGRESS },
            { label: "Completed", value: TestProgressStatus.COMPLETED },
          ],
          onChange: setStatusFilter,
        },
        refresh: {
          show: true,
          onRefresh: handleRefresh,
        },
      }}
    >
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        <div className="grid grid-cols-12 gap-6">
          {data?.tests.map((test) => (
            <TestCard key={test.id} test={test} onStart={handleStartTest} />
          ))}
        </div>
        <div className="mt-8">
          <PaginationNumeric
            currentPage={data?.currentPage}
            totalPages={data?.totalPages}
            onPageChange={setPage}
          />
        </div>
      </div>
    </AuthenticatedLayout>
  );
}
